import { PaymentPlanTable } from '@/components/Payouts/PaymentPlanTable';
import { isErrorWithData } from '@/services/helpers';
import { useGetPaymentPlansQuery } from '@/services/paymentPlans';
import { useUpdateProjectPaymentPlanMutation } from '@/services/payouts';
import { Button, Modal, toast, useOpenClose } from '@palmetto/palmetto-components';
import { useEffect, useState } from 'react';

import { AccountDocument, ClientPaymentPlan, ProjectDocument } from 'types';

import { ChangePlanConfirmationModal } from './ChangePlanConfirmationModal';
interface AccountPaymentPlanModalProps {
  isModalOpen: boolean;
  handleClose: () => void;
  project: ProjectDocument | undefined;
  paymentPlan: ClientPaymentPlan | undefined;
  refetchProjectAndTransactions: () => void;
  account: AccountDocument;
}

export const AccountPaymentPlanModal = ({
  isModalOpen,
  handleClose,
  project,
  paymentPlan,
  refetchProjectAndTransactions,
  account,
}: AccountPaymentPlanModalProps) => {
  const programType = paymentPlan?.programType ?? account.programType;
  const { data: paymentPlans, isLoading: isLoadingPaymentPlans } = useGetPaymentPlansQuery({ programType });
  const [paymentPlanId, setPaymentPlanId] = useState(String(paymentPlan?.id ?? ''));

  const {
    isOpen: isConfirmationModalOpen,
    handleOpen: handleOpenConfirmationModal,
    handleClose: handleCloseConfirmationModal,
  } = useOpenClose();

  useEffect(() => {
    setPaymentPlanId(String(paymentPlan?.id ?? ''));
  }, [setPaymentPlanId, paymentPlan?.id]);

  const [updateProjectPaymentPlan, { isLoading: isUpdating }] = useUpdateProjectPaymentPlanMutation();

  const handleConfirmPlanChange = async () => {
    try {
      await updateProjectPaymentPlan({
        planId: paymentPlanId,
        externalId: project?.externalId ?? String(account.id),
      }).unwrap();
      toast.success('Payment plan changed successfully');
      handleCloseConfirmationModal();
      handlePaymentPlanModalClose();
      refetchProjectAndTransactions();
    } catch (error) {
      handleCloseConfirmationModal();
      // Surface the API's reason (e.g. the milestone for this plan hasn't been reached yet)
      // instead of a generic message so the operator knows why the change was rejected.
      toast.error(isErrorWithData(error) ? error.data.message : `Could not change payment plan.`);
      console.error(error);
    }
  };

  const handlePaymentPlanModalClose = () => {
    setPaymentPlanId(String(paymentPlan?.id ?? ''));
    handleClose();
  };

  return (
    <>
      <ChangePlanConfirmationModal
        isOpen={isConfirmationModalOpen}
        isUpdating={isUpdating}
        onClose={handleCloseConfirmationModal}
        onConfirm={handleConfirmPlanChange}
      />
      <Modal isOpen={isModalOpen} onDismiss={handlePaymentPlanModalClose}>
        <Modal.Header id="modal-title" title="Change Plan" onDismiss={handlePaymentPlanModalClose} />
        {!isLoadingPaymentPlans && paymentPlans && (
          <Modal.Body>
            <PaymentPlanTable
              programType={programType}
              paymentPlans={paymentPlans}
              selectedPaymentPlanId={paymentPlanId}
              selectPaymentPlan={setPaymentPlanId}
            />
          </Modal.Body>
        )}

        <Modal.Footer>
          <Button variant="secondary" tone="neutral" onClick={handlePaymentPlanModalClose}>
            Cancel
          </Button>
          <Button isLoading={isUpdating} variant="primary" type="submit" onClick={handleOpenConfirmationModal}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
